import {
  Badge,
  Box,
  HStack,
  Code,
  Text,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  BadgeProps,
  Alert,
  AlertIcon,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useAdminTestClient } from '@duke-hq/app-admin-web/lib/adminTestClient';
import { JsonViewer } from '@duke-hq/ui/components/JsonViewer/JsonViewer';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { Link } from '../../components/Link';
import { SeedTerminal } from './SeedTerminal';

export function SeedRuns({
  isOpen,
  onClose,
  strategyName,
}: {
  isOpen: boolean;
  onClose: () => void;
  strategyName?: string;
}): React.ReactElement | null {
  if (!isOpen) {
    return null;
  }

  if (typeof strategyName === 'string') {
    return <SeedRunList strategyName={strategyName} isOpen={isOpen} onClose={onClose} />;
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader>
          <DrawerCloseButton onClick={onClose} />
        </DrawerHeader>
        <DrawerBody>
          <Alert status="warning">
            <AlertIcon />
            <Text>Please select a seed strategy to view its runs.</Text>
          </Alert>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export function SeedRunList({
  isOpen,
  onClose,
  strategyName,
}: {
  isOpen: boolean;
  onClose: () => void;
  strategyName: string;
}): React.ReactElement {
  const testClient = useAdminTestClient();
  const { data } = useQuery({
    queryKey: ['adminTestServer', 'seeds', 'listRuns', strategyName],
    queryFn: () => testClient.seeds.listRuns.query({ strategyName }),
  });

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader padding="0">
          <DrawerCloseButton onClick={onClose} />
        </DrawerHeader>
        <Box p="4">
          <Text fontSize="xl" fontWeight="bold">
            Seed Runs <Code>{strategyName}</Code>
          </Text>
        </Box>

        <DrawerBody p="0" border="none" m="0" paddingInline="0 !important">
          {typeof data !== 'undefined' && data.items.length > 0 ? (
            <Accordion allowMultiple defaultIndex={[0]}>
              {data.items.map((run) => {
                let statusColor: BadgeProps['colorScheme'];

                switch (run.status) {
                  case 'SUCCESS':
                    statusColor = 'green';
                    break;
                  case 'RUNNING':
                    statusColor = 'purple';
                    break;
                  case 'FAILURE':
                    statusColor = 'red';
                    break;
                  default:
                    statusColor = 'orange';
                }

                return (
                  <AccordionItem key={run.id}>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box as="span" flex="10" textAlign="left">
                              {run.id}
                            </Box>
                            <Badge flex="1" colorScheme={statusColor}>
                              {run.status}
                            </Badge>
                            {isExpanded ? <MinusIcon fontSize="xs" /> : <PlusIcon fontSize="xs" />}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                          <Box p={3} borderWidth="1px" borderRadius="md" mb={4}>
                            <VStack align="stretch" gap={2}>
                              {typeof run.traceUrl === 'string' ? (
                                <HStack>
                                  <Text fontWeight="bold" width={pxToRem(120)}>
                                    Trace:
                                  </Text>
                                  <Link to={run.traceUrl} target="_blank">
                                    {run.traceId}
                                  </Link>
                                </HStack>
                              ) : null}
                              <HStack>
                                <Text fontWeight="bold" width={pxToRem(120)}>
                                  Created at:
                                </Text>
                                <Text>{run.createdAt.toLocaleString()}</Text>
                              </HStack>
                              <HStack>
                                <Text fontWeight="bold" width={pxToRem(120)}>
                                  Finished at:
                                </Text>
                                <Text>
                                  {run.finishedAt !== null ? (
                                    run.finishedAt.toLocaleString()
                                  ) : (
                                    <Badge colorScheme="yellow">Not finished</Badge>
                                  )}
                                </Text>
                              </HStack>
                            </VStack>
                          </Box>
                          <VStack w="full">
                            {typeof run.input === 'string' ? (
                              <JsonViewer data={run.input} title="Input" />
                            ) : null}
                            {typeof run.result === 'string' ? (
                              <Box w="full">
                                <Text fontWeight="bold" mb={2}>
                                  Result
                                </Text>
                                <Box h="400px" overflowX="auto">
                                  <SeedTerminal chunks={[run.result]} />
                                </Box>
                              </Box>
                            ) : null}
                            {run.meta !== null ? (
                              <JsonViewer data={JSON.stringify(run.meta)} title="Meta" />
                            ) : null}
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                );
              })}
            </Accordion>
          ) : (
            <Alert status="info">
              <AlertIcon />
              <Text>
                No seed runs found for <Code>{strategyName}</Code>.
              </Text>
            </Alert>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
